import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { AuthInterceptor } from './auth.interceptor';
import { GrpcWrapperService } from './grpc-wrapper.service';

// Import the generated protobuf types and gRPC clients
import {
  ArchitecturalPlanServiceClient,
  ArchitecturalPlanWriteServiceClient,
} from '../../generated.commonjs/ApiServiceClientPb';
import {
  GetInputFileMetadataRequest,
  GetInputFileMetadataResponse,
  ListInputFileMetadataRequest,
  ListInputFileMetadataResponse,
  GenerateInputFileMetadataRequest,
  GenerateInputFileMetadataResponse,
  DeleteInputFileRequest,
  DeleteInputFileResponse,
  DocumentType,
  ProcessingStatus,
} from '../../generated.commonjs/api_pb';

// Re-export enums for easier use in components
export { DocumentType, ProcessingStatus };

/**
 * Service for managing file metadata via gRPC.
 * Handles retrieving, listing, and generating metadata for uploaded files.
 */
@Injectable({
  providedIn: 'root',
})
export class FileMetadataService {
  private environmentService = inject(EnvironmentService);
  private authInterceptor = inject(AuthInterceptor);
  private grpcWrapper = inject(GrpcWrapperService);

  private static instanceCounter = 0;
  private instanceId: number;
  private client: ArchitecturalPlanServiceClient;
  private writeClient: ArchitecturalPlanWriteServiceClient;

  constructor() {
    this.instanceId = ++FileMetadataService.instanceCounter;
    ClientLoggingService.log('INFO', `[FileMetadataService ${this.instanceId}] Initialized`);

    // Initialize gRPC client with authentication
    const apiServer = this.environmentService.getApiServer();
    ClientLoggingService.log('INFO', `[FileMetadataService ${this.instanceId}] Using gRPC API server: ${apiServer}`);

    // Configure gRPC client with auth interceptor
    const options = {
      unaryInterceptors: [this.authInterceptor],
    };

    this.client = new ArchitecturalPlanServiceClient(`https://${apiServer}:443`, null, options);
    this.writeClient = new ArchitecturalPlanWriteServiceClient(`https://${apiServer}:443`, null, options);
  }

  /**
   * Gets metadata for a specific file by file ID.
   * Returns an Observable that emits the file metadata.
   */
  getFileMetadata(
    projectId: string,
    fileId: string,
    autoGenerateIfMissing: boolean = true
  ): Observable<GetInputFileMetadataResponse> {
    ClientLoggingService.log(
      'INFO',
      `[FileMetadataService ${this.instanceId}] Getting metadata for file ${fileId} in project ${projectId}`
    );

    return new Observable<GetInputFileMetadataResponse>(observer => {
      try {
        // Create the request using protobuf
        const request = new GetInputFileMetadataRequest();
        request.setProjectId(projectId);
        request.setFileId(fileId);
        request.setAutoGenerateIfMissing(autoGenerateIfMissing);

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.getInputFileMetadata(request, null);

        call
          .then((response: GetInputFileMetadataResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[FileMetadataService ${this.instanceId}] Got metadata:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[FileMetadataService ${this.instanceId}] Failed to get metadata:` +
                ` code=${error?.code}, message=${error?.message}`,
              error
            );
            observer.error(error);
          });
      } catch (error: any) {
        ClientLoggingService.log(
          'ERROR',
          `[FileMetadataService ${this.instanceId}] Error getting file metadata:` +
            ` code=${error?.code}, message=${error?.message}`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Lists all file metadata for a project with optional filtering and pagination.
   * Returns an Observable that emits the list of file metadata.
   */
  listFileMetadata(
    projectId: string,
    pageSize: number = 50,
    pageToken?: string,
    documentTypeFilter?: DocumentType,
    statusFilter?: ProcessingStatus
  ): Observable<ListInputFileMetadataResponse> {
    ClientLoggingService.log(
      'INFO',
      `[FileMetadataService ${this.instanceId}] Listing metadata for project ${projectId}`
    );

    return new Observable<ListInputFileMetadataResponse>(observer => {
      try {
        // Create the request using protobuf
        const request = new ListInputFileMetadataRequest();
        request.setProjectId(projectId);
        request.setPageSize(pageSize);

        if (pageToken) {
          request.setPageToken(pageToken);
        }

        if (documentTypeFilter !== undefined) {
          request.setDocumentTypeFilter(documentTypeFilter);
        }

        if (statusFilter !== undefined) {
          request.setStatusFilter(statusFilter);
        }

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.listInputFileMetadata(request, null);

        call
          .then((response: ListInputFileMetadataResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[FileMetadataService ${this.instanceId}] Listed ${response.getFilesList().length} files:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[FileMetadataService ${this.instanceId}] Failed to list metadata:` +
                ` code=${error?.code}, message=${error?.message}`,
              error
            );
            observer.error(error);
          });
      } catch (error: any) {
        ClientLoggingService.log(
          'ERROR',
          `[FileMetadataService ${this.instanceId}] Error listing file metadata:` +
            ` code=${error?.code}, message=${error?.message}`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Generates metadata for a file (useful for backfilling existing files).
   * Returns an Observable that emits the generated metadata.
   */
  generateFileMetadata(
    projectId: string,
    inputFilePath: string,
    forceRegenerate: boolean = false
  ): Observable<GenerateInputFileMetadataResponse> {
    ClientLoggingService.log(
      'INFO',
      `[FileMetadataService ${this.instanceId}] Generating metadata for ${inputFilePath} in project ${projectId}`
    );

    return new Observable<GenerateInputFileMetadataResponse>(observer => {
      try {
        // Create the request using protobuf
        const request = new GenerateInputFileMetadataRequest();
        request.setProjectId(projectId);
        request.setInputFilePath(inputFilePath);
        request.setForceRegenerate(forceRegenerate);

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.client.generateInputFileMetadata(request, null);

        call
          .then((response: GenerateInputFileMetadataResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[FileMetadataService ${this.instanceId}] Generated metadata:`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[FileMetadataService ${this.instanceId}] Failed to generate metadata:` +
                ` code=${error?.code}, message=${error?.message}`,
              error
            );
            observer.error(error);
          });
      } catch (error: any) {
        ClientLoggingService.log(
          'ERROR',
          `[FileMetadataService ${this.instanceId}] Error generating file metadata:` +
            ` code=${error?.code}, message=${error?.message}`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Deletes a specific input file and its associated metadata.
   * Returns an Observable that emits the deletion response.
   */
  deleteInputFile(projectId: string, fileId: string): Observable<DeleteInputFileResponse> {
    ClientLoggingService.log(
      'INFO',
      `[FileMetadataService ${this.instanceId}] Deleting input file ${fileId} in project ${projectId}`
    );

    return new Observable<DeleteInputFileResponse>(observer => {
      try {
        // Create the request using protobuf
        const request = new DeleteInputFileRequest();
        request.setProjectId(projectId);
        request.setFileId(fileId);

        // Make the gRPC call (authentication handled by interceptor)
        const call = this.writeClient.deleteInputFile(request, null);

        call
          .then((response: DeleteInputFileResponse) => {
            ClientLoggingService.log(
              'INFO',
              `[FileMetadataService ${this.instanceId}] Deleted input file successfully`,
              response.toObject()
            );
            observer.next(response);
            observer.complete();
          })
          .catch((error: any) => {
            ClientLoggingService.log(
              'ERROR',
              `[FileMetadataService ${this.instanceId}] Failed to delete input file:` +
                ` code=${error?.code}, message=${error?.message}`,
              error
            );
            observer.error(error);
          });
      } catch (error: any) {
        ClientLoggingService.log(
          'ERROR',
          `[FileMetadataService ${this.instanceId}] Error deleting input file:` +
            ` code=${error?.code}, message=${error?.message}`,
          error
        );
        observer.error(error);
      }
    });
  }

  /**
   * Utility method to format file size in human-readable format.
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Utility method to format document type for display.
   */
  formatDocumentType(documentType: DocumentType): string {
    switch (documentType) {
      case DocumentType.DOCUMENT_TYPE_ARCHITECTURAL_PLAN:
        return 'Architectural Plan';
      case DocumentType.DOCUMENT_TYPE_MECHANICAL_PLAN:
        return 'Mechanical Plan';
      case DocumentType.DOCUMENT_TYPE_ELECTRICAL_PLAN:
        return 'Electrical Plan';
      case DocumentType.DOCUMENT_TYPE_STRUCTURAL_PLAN:
        return 'Structural Plan';
      case DocumentType.DOCUMENT_TYPE_INSPECTOR_FEEDBACK:
        return 'Inspector Feedback';
      case DocumentType.DOCUMENT_TYPE_PERMIT_APPLICATION:
        return 'Permit Application';
      case DocumentType.DOCUMENT_TYPE_CODE_COMPLIANCE_REPORT:
        return 'Code Compliance Report';
      case DocumentType.DOCUMENT_TYPE_SITE_PLAN:
        return 'Site Plan';
      case DocumentType.DOCUMENT_TYPE_ELEVATION_DRAWING:
        return 'Elevation Drawing';
      case DocumentType.DOCUMENT_TYPE_SECTION_DRAWING:
        return 'Section Drawing';
      default:
        return 'Unknown';
    }
  }

  /**
   * Utility method to format processing status for display.
   */
  formatProcessingStatus(status: ProcessingStatus): string {
    switch (status) {
      case ProcessingStatus.PROCESSING_STATUS_UPLOADED:
        return 'Uploaded';
      case ProcessingStatus.PROCESSING_STATUS_PROCESSING:
        return 'Processing';
      case ProcessingStatus.PROCESSING_STATUS_COMPLETED:
        return 'Completed';
      case ProcessingStatus.PROCESSING_STATUS_FAILED:
        return 'Failed';
      default:
        return 'Unknown';
    }
  }

  /**
   * Utility method to get status color class for UI styling.
   */
  getStatusColorClass(status: ProcessingStatus): string {
    switch (status) {
      case ProcessingStatus.PROCESSING_STATUS_UPLOADED:
        return 'status-uploaded';
      case ProcessingStatus.PROCESSING_STATUS_PROCESSING:
        return 'status-processing';
      case ProcessingStatus.PROCESSING_STATUS_COMPLETED:
        return 'status-completed';
      case ProcessingStatus.PROCESSING_STATUS_FAILED:
        return 'status-failed';
      default:
        return 'status-unknown';
    }
  }
}
